import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";

export type AccessCodeCourtRow = {
    courtId: number;
    courtName: string;
    code: string | null;
    updatedAt: string | null;
    updatedByEmail: string | null;
};

export type AccessCodesForOrganization = {
    accessCodeRevealLeadMinutes: number;
    accessCodeGraceMinutes: number;
    accessCodeNotifyLeadMinutes: number | null;
    courts: AccessCodeCourtRow[];
};

export type BatchUpdateAccessCodesInput = {
    accessCodeRevealLeadMinutes: number;
    accessCodeGraceMinutes: number;
    accessCodeNotifyLeadMinutes: number | null;
    courts: { courtId: number; code: string | null }[];
};

export const ORG_ACCESS_CODES_QUERY_KEY = (orgId: string) => ['organization', orgId, 'access-codes'];

export const useGetAccessCodes = (organizationId: string | undefined, enabled = true) => {
    const apiClient = useApiClient();
    return useQuery({
        enabled: enabled && !!organizationId,
        queryKey: ORG_ACCESS_CODES_QUERY_KEY(organizationId ?? ''),
        queryFn: () => apiClient.fetchApi<AccessCodesForOrganization, {}>({
            method: 'GET',
            endpoint: `organizations/${organizationId}/access-codes`,
        }),
    });
};

export const useBatchUpdateAccessCodes = (organizationId: string | undefined) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['batchUpdateAccessCodes', organizationId],
        mutationFn: (input: BatchUpdateAccessCodesInput) =>
            apiClient.fetchApi<AccessCodesForOrganization, BatchUpdateAccessCodesInput>({
                method: 'POST',
                endpoint: `organizations/${organizationId}/access-codes/batch`,
                body: input,
            }),
        onSuccess: (data) => {
            if (organizationId) {
                queryClient.setQueryData(ORG_ACCESS_CODES_QUERY_KEY(organizationId), data);
            }
        },
    });
};
